import React, { useState } from "react";
import Minus from "images/Minus.svg";
import Plus from "images/Plus.svg";
import NcImage from "shared/NcImage/NcImage";

interface SectionProps {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const Section: React.FC<SectionProps> = ({
  title,
  children,
  isOpen,
  onToggle,
}) => {
  return (
    <div className="border my-4 bg-[#FFFFFF0D] rounded-2xl p-6">
      <button
        onClick={onToggle}
        className="flex justify-between w-full px-4 pt-3 text-left items-center"
      >
        <span className="text-xl font-semibold leading-7 text-left text-white">
          {title}
        </span>
        <span>
          {isOpen ? (
            <span className="text-4xl"> - </span>
          ) : (
            <span className="text-4xl"> + </span>
          )}
        </span>
      </button>
      {isOpen && <div className="px-4 py-2">{children}</div>}
    </div>
  );
};

const RentalPoliciesPage: React.FC = () => {
  const [openSectionIndex, setOpenSectionIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenSectionIndex(openSectionIndex === index ? null : index);
  };
  return (
    <>
      <div className="flex justify-center mb-16">
        <span className="text-4xl text-white leading-10 text-center tracking-tight">
          Frequently Asked Questions
        </span>
      </div>
      <div className=" mx-auto mt-4">
        <Section
          title="What's the minimum age to rent?"
          isOpen={openSectionIndex === 1}
          onToggle={() => handleToggle(1)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            The minimum age requirement for renting a car from AMK Elite Car Rentals
            Enterprise is 21 years old
          </span>
        </Section>
        <Section
          title="What are the requirements to rent?"
          isOpen={openSectionIndex === 2}
          onToggle={() => handleToggle(2)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            <ul className="list-disc ml-5 text-[#D2CFCE]">
              <li>A Valid Driver’s License</li>
              <li>A Security Deposit</li>
              <li>A Clean Driving Record</li>
              <li>To Meet Rental Age Requirements</li>
              <li>
                Your Own Full-Coverage Insurance Policy or You Can Purchase Ours
                on Website
              </li>
              <li>To Acknowledge & Sign Our Rental Agreement</li>
            </ul>
          </span>
        </Section>
        <Section
          title="What is needed to rent a vehicle?"
          isOpen={openSectionIndex === 3}
          onToggle={() => handleToggle(3)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            In order to rent all renters must have a valid driver’s license
            within the United States, must be at least 21, and hold an active
            full coverage insurance policy. If you don't possess a full coverage
            insurance policy you may visit our website's booking page to explore
            other available insurance options.
          </span>
        </Section>
        <Section
          title="Times To Book?"
          isOpen={openSectionIndex === 4}
          onToggle={() => handleToggle(4)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            Our standard hours of operation are from 7 AM to 9 PM. To
            accommodate your needs, we also provide the option for after hour
            booking allowing you to request a vehicle outside of these standard
            hours for an additional fee.
          </span>
        </Section>
        <Section
          title="Shortest and Longest Trip?"
          isOpen={openSectionIndex === 5}
          onToggle={() => handleToggle(5)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            At AMK Elite Car Rentals Enterprise, we offer a range of discounts for daily,
            weekly, and monthly rentals. Please note that the shortest rental
            duration is one day. For monthly rentals, a renewal of the rental
            agreement is required every 29 days. Additionally, to ensure our
            vehicles are properly maintained and sanitized, a mandatory 1-hour
            grace period is required before the start of each reservation.
          </span>
        </Section>
        <Section
          title="Why a security deposit?"
          isOpen={openSectionIndex === 6}
          onToggle={() => handleToggle(6)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            A security deposit is required to cover potential damages, fuel
            discrepancies, traffic violations, or toll charges incurred during
            the rental period. It also ensures the vehicle is returned in good
            condition and on time. The deposit helps protect the company from
            risks like fraud or non-payment. If the vehicle is returned without
            any issues, the deposit is fully refundable.
          </span>
        </Section>
        <Section
          title="Cancelation Policy"
          isOpen={openSectionIndex === 7}
          onToggle={() => handleToggle(7)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            We understand that plans can change, which is why we offer a
            flexible cancellation policy. If you need to cancel your
            reservation, please let us know at least 24 hours before the
            scheduled pick-up time. Please refer to our Cancellation Policy page
            for detailed information and instructions to ensure full
            understanding of our terms and procedures.
          </span>
        </Section>
        <Section
          title="May I have the car delivered to me?"
          isOpen={openSectionIndex === 8}
          onToggle={() => handleToggle(8)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            You may choose to get your vehicle delivered directly to your home,
            office, hotel, airport, or other location at an additional cost.
            Please note: All pickup & delivery arrangements must be confirmed at
            least 24 hours in advance in order to guarantee on-time service. Any
            changes to pickup or delivery times/locations with less than 24
            hours notice may incur an additional transportation fee.
          </span>
        </Section>
        <Section
          title="What times can I get the car delivered?"
          isOpen={openSectionIndex === 9}
          onToggle={() => handleToggle(9)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            Our normal delivery hours are between 9am and 7pm. Any deliveries
            outside of those hours must be pre-arranged with us. We’ll do our
            best to work around your schedule.
          </span>
        </Section>
        <Section
          title="What is the booking process like?"
          isOpen={openSectionIndex === 10}
          onToggle={() => handleToggle(10)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            Our booking process is quick and easy. Simply visit our website,
            browse our fleet of vehicles, and select the car that suits your
            needs. You can also contact our customer service team to help you
            choose the perfect car for your needs.
          </span>
        </Section>
        <Section
          title="When do I pay for the trip?"
          isOpen={openSectionIndex === 11}
          onToggle={() => handleToggle(11)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            You will have the ability to pay online prior to the start of the
            trip. The full price including the security deposit MUST be paid
            before you receive the keys. If you incur additional fees such as
            mileage overages, parking tickets, cleaning fees, etc. you will be
            charged after the trip ends. We will deduct from the security
            deposit prior to charging the card on file.
          </span>
        </Section>
        <Section
          title="What form of payment do you accept?"
          isOpen={openSectionIndex === 12}
          onToggle={() => handleToggle(12)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            We offer a variety of convenient payment options, including all
            major credit cards, debit cards, wire transfers, and cash. Fees and
            Taxes may apply
          </span>
        </Section>
        <Section
          title="How far in advance should I book?"
          isOpen={openSectionIndex === 13}
          onToggle={() => handleToggle(13)}
        >
          <span className="text-base font-normal leading-6 text-left text-[#D2CFCE]">
            We recommend booking as early as possible, especially for weekends
            and special events. This helps guarantee that your dream car is
            available for your desired date.
          </span>
        </Section>
      </div>
    </>
  );
};

export default RentalPoliciesPage;
